import { Component, forwardRef } from 'react';

import { ActivableRendererProps } from './ActivableRendererProps';

interface ActivableRendererState {
    active: boolean;
    rendered: boolean;
}

export interface FactoryProps {
    delay?: number;
    activePropName?: string;
}

export default <T extends React.Component, OriginalProps extends ActivableRendererProps>({
        delay = 500,
        activePropName = 'active',
    }: FactoryProps = {}) =>
    (ActivableComponent: React.ComponentType<OriginalProps>) => {
        interface PrivateProps extends ActivableRendererProps {
            forwardedRef?: React.Ref<T>;
        }

        type Props = OriginalProps;
        interface IndexedProps {
            [key: string]: boolean;
        }

        class ActivableRenderer extends Component<Props, ActivableRendererState> {
            readonly state = {
                active: (this.props as unknown as IndexedProps)[activePropName],
                rendered: (this.props as unknown as IndexedProps)[activePropName],
            };

            private activateFrame?: number;
            private unrenderTimeout?: number;

            UNSAFE_componentWillReceiveProps(nextProps: Props) {
                // All your bases are belong to us
                const nextIndexedProps = nextProps as unknown as IndexedProps;
                const currentProps = this.props as unknown as IndexedProps;

                if (nextIndexedProps[activePropName] && !currentProps[activePropName]) {
                    this.activate();
                }

                if (!nextIndexedProps[activePropName] && currentProps[activePropName]) {
                    this.deactivate();
                }
            }

            componentWillUnmount() {
                this.clearActivateTimeout();
                this.clearUnrenderTimeout();
            }

            activate() {
                const { onMount, onActivationFinished } = this.props;

                this.clearUnrenderTimeout();
                this.activateFrame = window.requestAnimationFrame(() => {
                    this.setState({ rendered: true, active: false }, () => {
                        if (onMount) {
                            onMount();
                        }

                        this.activateFrame = window.requestAnimationFrame(() => {
                            this.setState({ active: true }, () => {
                                if (onActivationFinished) {
                                    onActivationFinished();
                                }
                            });
                            this.activateFrame = undefined;
                        });
                    });
                });
            }

            deactivate() {
                const { onUnmount, onDeactivationFinished } = this.props;

                this.setState({ rendered: true, active: false }, () => {
                    if (onDeactivationFinished) {
                        onDeactivationFinished();
                    }

                    this.unrenderTimeout = window.setTimeout(() => {
                        this.setState({ rendered: false }, () => {
                            if (onUnmount) {
                                onUnmount();
                            }
                        });
                        this.unrenderTimeout = undefined;
                    }, delay);
                });
            }

            clearActivateTimeout() {
                if (this.activateFrame) {
                    window.cancelAnimationFrame(this.activateFrame);
                    this.activateFrame = undefined;
                }
            }

            clearUnrenderTimeout() {
                if (this.unrenderTimeout) {
                    window.clearTimeout(this.unrenderTimeout);
                    this.unrenderTimeout = undefined;
                }
            }

            render() {
                const { forwardedRef, onMount, ...other } = this.props as PrivateProps;
                const { active, rendered } = this.state;

                const props = {
                    ref: forwardedRef,
                    [activePropName]: active,
                };

                return rendered ? <ActivableComponent {...(other as OriginalProps)} {...props} /> : null;
            }
        }

        return forwardRef<T, OriginalProps>((props, ref) => <ActivableRenderer {...props} forwardedRef={ref} />);
    };
